import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { createDiscount, updateDiscount, listPlans, Discount, Plan } from './api';
import { useToast } from '../../context/ToastContext';

const inputCls =
  'w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-content outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/30';
const labelCls = 'mb-1 block text-xs font-medium uppercase tracking-wide text-content-3';

export default function DiscountForm({ discount, onClose, onSaved }: { discount?: Discount | null; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const editing = !!discount;
  const [name, setName] = useState(discount?.name ?? '');
  const [code, setCode] = useState(discount?.code ?? '');
  const [isPct, setIsPct] = useState(discount ? !!discount.is_percentage : false);
  const [value, setValue] = useState(discount?.value != null ? String(discount.value) : '');
  const [membershipId, setMembershipId] = useState(discount?.membership_id != null ? String(discount.membership_id) : '');
  const [duration, setDuration] = useState(discount?.duration_months != null ? String(discount.duration_months) : '');
  const [plans, setPlans] = useState<Plan[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { listPlans().then((r) => setPlans(r.data)); }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) { toast.error('Ponle un nombre al descuento'); return; }
    const v = Number(value);
    if (!(v > 0)) { toast.error('Indica un valor mayor a 0'); return; }
    if (isPct && v > 100) { toast.error('Un porcentaje no puede ser mayor a 100'); return; }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(), code: code.trim() || null, value: v, is_percentage: isPct ? 1 : 0,
        membership_id: membershipId === '' ? null : Number(membershipId),
        duration_months: duration === '' ? null : Number(duration),
      };
      if (editing) { await updateDiscount(discount!.discount_id, payload); toast.success('Descuento actualizado'); }
      else { await createDiscount(payload); toast.success('Descuento creado'); }
      onSaved();
    } catch (err: any) { toast.error(err?.message || 'No se pudo guardar el descuento'); }
    finally { setSaving(false); }
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" role="dialog" aria-modal="true">
      <div className="modal-overlay absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="modal-panel relative max-h-[92vh] w-full max-w-md overflow-y-auto rounded-t-2xl border border-line bg-surface p-5 shadow-xl sm:rounded-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-content">{editing ? 'Editar descuento' : 'Nuevo descuento'}</h3>
          <button onClick={onClose} className="text-content-3 transition hover:text-content" aria-label="Cerrar"><X size={18} /></button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className={labelCls}>Nombre del descuento</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ej. Dto. 2 disciplinas" className={inputCls} autoFocus />
          </div>
          <div>
            <label className={labelCls}>Clave (opcional)</label>
            <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Ej. D2D" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Tipo</label>
            <div className="inline-flex rounded-lg border border-line bg-surface p-1">
              <button type="button" onClick={() => setIsPct(false)}
                className={`rounded-md px-3 py-1 text-sm font-medium transition ${!isPct ? 'bg-accent text-on-accent shadow-sm' : 'text-content-2'}`}>Monto fijo ($)</button>
              <button type="button" onClick={() => setIsPct(true)}
                className={`rounded-md px-3 py-1 text-sm font-medium transition ${isPct ? 'bg-accent text-on-accent shadow-sm' : 'text-content-2'}`}>Porcentaje (%)</button>
            </div>
          </div>
          <div>
            <label className={labelCls}>{isPct ? 'Porcentaje a descontar' : 'Monto a descontar (MXN)'}</label>
            <div className="relative">
              <input type="number" inputMode="decimal" step="0.01" min="0" max={isPct ? '100' : undefined}
                value={value} onChange={(e) => setValue(e.target.value)} placeholder={isPct ? 'Ej. 50' : 'Ej. 100'} className={inputCls} />
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-content-3">{isPct ? '%' : '$'}</span>
            </div>
            <p className="mt-1 text-xs text-content-3">Se resta de la mensualidad del plan al cobrar.</p>
          </div>

          <div>
            <label className={labelCls}>Aplica al plan</label>
            <select value={membershipId} onChange={(e) => setMembershipId(e.target.value)} className={inputCls}>
              <option value="">Cualquier plan (general)</option>
              {plans.map((p) => <option key={p.membership_id} value={p.membership_id}>{p.name}</option>)}
            </select>
            <p className="mt-1 text-xs text-content-3">Si lo ligas a un plan, en la ficha solo se sugiere para ese plan. Déjalo en "Cualquier plan" para descuentos generales (−$50, becas, etc.).</p>
          </div>

          <div>
            <label className={labelCls}>Vigencia (opcional)</label>
            <div className="flex items-center gap-2">
              <input type="number" inputMode="numeric" min="0" value={duration} onChange={(e) => setDuration(e.target.value)} placeholder="—" className={`${inputCls} w-24`} />
              <span className="text-sm text-content-2">meses</span>
            </div>
            <p className="mt-1 text-xs text-content-3">Para promos temporales. Vacío = permanente (aplica cada mes hasta quitarlo). Al asignarlo a un alumno, propone una fecha de vencimiento con esta duración.</p>
          </div>

          <div className="flex items-center justify-end gap-3 pt-1">
            <button type="button" onClick={onClose} className="rounded-lg border border-line bg-surface px-4 py-2 text-sm text-content-2 transition hover:bg-surface-2">Cancelar</button>
            <button type="submit" disabled={saving}
              className="rounded-lg bg-accent-solid px-4 py-2 text-sm font-semibold text-on-accent transition hover:bg-accent-solid/90 disabled:bg-line disabled:text-content-3">
              {saving ? 'Guardando…' : editing ? 'Guardar' : 'Crear descuento'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
